<template>
  <div
    v-if="visible"
    v-tooltip.html.placementRight="tooltipHtml"
    class="
      tw-group
      tw-absolute
      tw-inset-y-0
      tw-right-0
      tw-z-20
      tw-flex
      tw-w-2
      tw-translate-x-1/2
      tw-cursor-col-resize
      tw-touch-none
      tw-select-none
      tw-items-stretch
      tw-justify-center
      focus-visible:tw-outline-none
    "
    :data-dragging="isDragging ? 'true' : 'false'"
    role="separator"
    aria-orientation="vertical"
    :aria-label="I18n.t('report.drawer.resize')"
    :aria-controls="REPORT_GRAPHS_DRAWER_ID"
    :aria-valuenow="widthPct ?? undefined"
    :aria-valuemin="REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT"
    :aria-valuemax="REPORT_GRAPHS_DRAWER_MAX_WIDTH_PCT"
    tabindex="0"
    @pointerdown="onPointerDown"
    @keydown="onKeyDown"
    @focus="onFocus"
  >
    <span
      class="
        tw-w-px tw-bg-sf-neutral-lighter-2 tw-transition-all
        group-hover:tw-w-0.5
        group-hover:tw-bg-sf-primary
        group-focus-visible:tw-w-0.5
        group-focus-visible:tw-bg-sf-primary
        group-data-[dragging=true]:tw-w-0.5
        group-data-[dragging=true]:tw-bg-sf-primary
      "
    />
    <span
      aria-hidden="true"
      class="
        tw-absolute
        tw-left-1/2
        tw-top-1/2
        tw-h-11
        tw-w-1.5
        tw--translate-x-1/2
        tw--translate-y-1/2
        tw-rounded-full
        tw-bg-sf-primary
        tw-opacity-0
        tw-transition-opacity
        group-hover:tw-opacity-100
        group-focus-visible:tw-opacity-100
        group-data-[dragging=true]:tw-opacity-100
      "
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useEventListener } from '@vueuse/core'

import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import {
  setWidthPct,
  readWidthPct,
  beginDragMeasurement,
  REPORT_GRAPHS_DRAWER_ID,
} from '@app/application/report/utils/report-pane-layout'
import {
  getLeftDrawerHotkey,
  getHotkeyKbd,
} from '@app/application/behaviors/drawers/hotkeys'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import {
  REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT,
  REPORT_GRAPHS_DRAWER_MAX_WIDTH_PCT,
} from '@app/application/report/constants'

const I18n = useI18n()
const store = useReportGraphsDrawer()

// New-layout only, where the pane is `aside#secondary-left-drawer`. The handle
// renders inside the pane, absolutely positioned on its right edge, so CSS
// keeps it aligned through width changes and transitions. Hidden while the
// pane is collapsed or fullscreen — no boundary to drag.
const usingNewLayout = isUsingNewLayout()
const visible = computed(
  () => usingNewLayout && store.isOpen && !store.fullscreen
)

// A short click (no meaningful drag) collapses the drawer; the advertised
// ⌥/Alt toggle hotkey is owned by `report-graphs-drawer.vue`.
const CLICK_THRESHOLD_PX = 4
const KEYBOARD_STEP_PCT = 2
const isMac = navigator.userAgent.toUpperCase().includes('MAC')

const isDragging = ref(false)
let startX = 0
let dragMoved = false
// Geometry snapshot for the active drag, taken on pointerdown; null when the
// Slim layout elements are missing (then a click can still collapse).
let dragMeasure: ((clientX: number) => number) | null = null

// Pane width as a % of the wrapper (aria-valuenow + keyboard baseline):
// seeded from geometry on mount and focus, then tracked from applied values.
const widthPct = ref<number | null>(null)

// '' while dragging disposes the tooltip so it can't flicker under the
// moving handle.
const tooltipHtml = computed(() => {
  if (isDragging.value) return ''
  const kbd = getHotkeyKbd(`${isMac ? '⌥' : 'Alt '}${getLeftDrawerHotkey()}`)
  return `<div class="tw-text-left tw-leading-snug">
    <div>${I18n.t('report.drawer.resize_collapse_hint')} ${kbd}</div>
    <div class="tw-text-sf-neutral-light-1">${I18n.t(
      'report.drawer.resize'
    )}</div>
  </div>`
})

// Window pointer listeners live for the component's lifetime and no-op
// unless a drag is in progress.
function onPointerMove(event: PointerEvent): void {
  if (!isDragging.value) return
  if (Math.abs(event.clientX - startX) > CLICK_THRESHOLD_PX) dragMoved = true
  // Below the click threshold nothing resizes, so a click that wobbles a few
  // pixels can't nudge the width before collapsing.
  if (!dragMoved || !dragMeasure) return
  widthPct.value = setWidthPct(dragMeasure(event.clientX), { persist: false })
}

function endDrag(): void {
  isDragging.value = false
}

function onPointerUp(event: PointerEvent): void {
  if (!isDragging.value) return
  endDrag()

  if (!dragMoved) {
    // A click, not a drag: collapse the drawer.
    store.closeDrawer()
    return
  }

  if (dragMeasure) {
    widthPct.value = setWidthPct(dragMeasure(event.clientX), { persist: true })
  }
}

// A cancelled stream (e.g. touch gesture takeover) abandons the drag:
// no collapse, no persist.
function onPointerCancel(): void {
  if (!isDragging.value) return
  endDrag()
}

function onPointerDown(event: PointerEvent): void {
  if (event.button !== 0) return
  event.preventDefault()
  startX = event.clientX
  dragMoved = false
  dragMeasure = beginDragMeasurement()
  isDragging.value = true
  // Capture guarantees pointerup/pointercancel delivery even when released
  // outside the window; guarded because jsdom MouseEvents have no pointerId.
  if (event.pointerId !== undefined) {
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  }
}

// ARIA window-splitter keyboard pattern: arrows step the width, Home/End
// jump to the bounds, Enter collapses.
function onKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    event.preventDefault()
    store.closeDrawer()
    return
  }

  const current = widthPct.value
  if (current === null) return

  let target: number
  switch (event.key) {
    case 'ArrowLeft':
      target = current - KEYBOARD_STEP_PCT
      break
    case 'ArrowRight':
      target = current + KEYBOARD_STEP_PCT
      break
    case 'Home':
      target = REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT
      break
    case 'End':
      target = REPORT_GRAPHS_DRAWER_MAX_WIDTH_PCT
      break
    default:
      return
  }

  event.preventDefault()
  widthPct.value = setWidthPct(target, { persist: true })
}

function onFocus(): void {
  widthPct.value = readWidthPct()
}

useEventListener(window, 'pointermove', onPointerMove)
useEventListener(window, 'pointerup', onPointerUp)
useEventListener(window, 'pointercancel', onPointerCancel)

// Page-wide drag mode: keeps the resize cursor and suppresses text selection
// while the pointer is off the thin handle.
watch(isDragging, (dragging) => {
  document.body.classList.toggle('report-pane-resizing', dragging)
})

onMounted(() => {
  widthPct.value = readWidthPct()
})

onBeforeUnmount(() => {
  // Safety net: `watch` won't fire on teardown, so clear a lingering drag class.
  document.body.classList.remove('report-pane-resizing')
})
</script>

<style lang="scss" scoped>
// Cross-element drag overrides — the targets are outside this template, so
// Tailwind utilities can't express them.
:global(body.report-pane-resizing) {
  cursor: col-resize !important;
  user-select: none !important;
}

// Suppress the layout's width transitions during a drag so the panes track
// the cursor in real time.
:global(body.report-pane-resizing #secondary-left-drawer),
:global(body.report-pane-resizing #main-content-pane) {
  transition: none !important;
}
</style>
